import apiClient from "../../api/api";
import { removeSessionItem, setSessionItem } from "../../utils/helper/session";

/**
 * Decodes a JWT token and returns its payload as a JavaScript object.
 *
 * @function decodeJwt
 * @param {string} token - JWT token to be decoded.
 * @returns {Object|null} Decoded token payload, or null if decoding fails.
 *
 * @example
 * const user = decodeJwt(token);
 */
  function decodeJwt(token) {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );

      return JSON.parse(jsonPayload);
    } catch (err) {
      console.error("Error decoding JWT:", err);
      return null;
    }
  }

/**
 * Performs user authentication and initializes session data.
 *
 * This method:
 * - Sends login credentials to the authentication API
 * - Stores the returned JWT token in sessionStorage
 * - Sets a default profile image
 * - Decodes the JWT to retrieve user information
 * - Persists the user data in sessionStorage
 *
 * @async
 * @function requestAuthLogin
 * @param {Object} credentials - Login credentials payload (e.g. email and password).
 * @returns {Promise<void>} Resolves when the login flow is completed.
 *
 * @example
 * await requestAuthLogin({ email: "user@email.com", password: "123456" });
 */
export const requestAuthLogin = async(credentials) => {
  const loginResponse = await apiClient.post(import.meta.env.VITE_AUTH_LOGIN_REQUEST, credentials)
  const token = loginResponse.data.token
  const image = loginResponse.data.image
  const trolleyIdvStatus = loginResponse.data.trolleyIdvStatus
  sessionStorage.setItem("token", token)
  setSessionItem("trolleyIdvStatus", trolleyIdvStatus);
  if (image && image.trim() !== "") {
    sessionStorage.setItem("profileImage", image);
  } else {
    removeSessionItem("profileImage");
  }
  
  
  const user = decodeJwt(token)
  setSessionItem("User", user)
}


/**
 * Performs applicants authentication and initializes session data.
 *
 * This method:
 * - Sends login credentials to the authentication API
 * - Stores the returned JWT token in sessionStorage
 * - Sets a default profile image
 * - Decodes the JWT to retrieve applicants information
 * - Persists the applicants data in sessionStorage
 *
 * @async
 * @function requestAuthApplicantLogin
 * @param {Object} credentials - Login credentials payload (e.g. email and password).
 * @returns {Promise<void>} Resolves when the login flow is completed.
 *
 * @example
 * await requestAuthApplicantLogin({ email: "user@email.com", password: "123456" });
 */
export const requestAuthApplicantLogin = async(credentials) => {
  const loginResponse = await apiClient.post(import.meta.env.VITE_AUTH_APPLICANT_LOGIN_REQUEST, credentials)
  const token = loginResponse.data.token
  const image = loginResponse.data.image
  sessionStorage.setItem("token", token)
  if (image && image.trim() !== "") {
    sessionStorage.setItem("profileImage", image);
  } else {
    removeSessionItem("profileImage");
  }
  
  
  const user = decodeJwt(token)
  setSessionItem("User", user)
}

/**
 * Sends a password reset request for the given email.
 *
 * This method:
 * - Sends the user's email to the reset password endpoint
 * - Triggers a success toast notification when the request succeeds
 *
 * @async
 * @function requestResetPassword
 * @param {string} email - The email address associated with the user account.
 * @returns {Promise<void>} Resolves when the reset password request is completed.
 *
 * @example
 * await requestResetPassword("user@sirius-support.io");
 */
export const requestResetPassword = async(email) => {
  const payload = {
    email
  }
  await apiClient.post(import.meta.env.VITE_AUTH_RESET_PASSWORD_REQUEST, payload, {successToast: "An email was sent to your mailbox"})
  
}

/**
 * Updates the user's password.
 *
 * This method:
 * - Sends the user's email and new password to the update password endpoint
 * - Triggers a success toast notification when the request succeeds
 *
 * @async
 * @function requestUpdatePassword
 * @param {string} userEmail - The email address of the user whose password will be updated.
 * @param {string} password - The new password to be set.
 * @returns {Promise<void>} Resolves when the password update request is completed.
 *
 * @example
 * await requestUpdatePassword("user@sirius-support.io", "NewStrongPassword123");
 */
export const requestUpdatePassword = async(email, otp, newPassword) => {
  const payload = {
    email,
    otp,
    newPassword
  }
  await apiClient.post(import.meta.env.VITE_AUTH_UPDATE_PASSWORD_REQUEST, payload, {successToast: "Your password was updated successfully"})

}

export const authMeRequest = async() => {
  const response = await apiClient.get(import.meta.env.VITE_AUTH_ME_REQUEST)
  return response.data
}